import React, { useMemo } from 'react';

import { type OperationVariables } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Alert, Stack, Typography } from '@mui/material';
import type {
  DataGridPremiumProps,
  GridColDef,
  GridRowsProp,
} from '@mui/x-data-grid-premium';

import {
  type GetPropertySearchesListQuery,
  Order_By,
  type Property_Searches_Bool_Exp,
  type Property_Searches_Order_By,
} from '@graphql/__generated__/graphql';
import { RaDataGrid } from '@modules/_shared/components/data-grid/RaDataGrid';
import { UserInfo } from '@modules/_shared/components/data-grid/UserInfo';
import { type QuickFilterDefinition } from '@modules/_shared/components/filters/Filters';
import {
  ListToolbar,
  type ListToolbarProps,
} from '@modules/_shared/components/list-toolbar/ListToolbar';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import { TopbarTitle } from '@modules/_shared/components/topbar/Topbar';
import { formatRange } from '@modules/_shared/utils/formatting';
import {
  parseOrderByParam,
  withTiebreaker,
} from '@modules/_shared/utils/listUrlParams';
import { useScopedSearchParams } from '@modules/_shared/utils/navigation';
import { getPaginationVariables } from '@modules/_shared/utils/paginations';
import {
  GET_PROPERTY_SEARCHES_COUNT,
  GET_PROPERTY_SEARCHES_DICTIONARY,
  GET_PROPERTY_SEARCHES_LIST,
} from '@modules/property-searches/propertySearchesQueries';
import { useLocale } from '@shared/hooks/locale';

type PropertySearch = GetPropertySearchesListQuery['property_searches'][number];

const usePropertySearchParams = (
  defaultFilters?: Property_Searches_Bool_Exp,
  queryParamsScope?: string,
) => {
  const [searchParams] = useScopedSearchParams(queryParamsScope);
  const where: Property_Searches_Bool_Exp = JSON.parse(
    searchParams.get('where') || '{}',
  );
  const order_by = withTiebreaker<Property_Searches_Order_By>(
    parseOrderByParam<Property_Searches_Order_By>(
      searchParams.get('order_by'),
      [{ created_at: Order_By.Desc }],
    ),
    { id: Order_By.Asc },
    'id',
  );

  const { limit, offset, countLimit } = getPaginationVariables(
    searchParams,
    undefined,
    true,
  );
  const editMode = searchParams.get('edit') === 'true';

  if (defaultFilters != null) {
    where._and = [
      ...(where._and ?? []),
      ...Object.entries(defaultFilters).reduce((acc, [key, value]) => {
        acc.push({ [key]: value });

        return acc;
      }, [] as Property_Searches_Bool_Exp[]),
    ];
  }

  return {
    where,
    order_by,
    limit,
    offset,
    editMode,
    countLimit,
  };
};

export type PropertySearchListRefetchFn = (
  variables?: Partial<OperationVariables> | undefined,
) => Promise<unknown>;

type PropertySearchesListProps = {
  bulkActions?: (props: {
    refetch: PropertySearchListRefetchFn;
  }) => React.ReactNode;
  defaultFilters?: Property_Searches_Bool_Exp;
  queryParamsScope?: string;
  forceEditMode?: boolean;
  toolbarActions?: React.ReactNode;
  onSelectionChange?: (selectedRows: PropertySearch[]) => void;
  quickFilters?: QuickFilterDefinition[];
  hideTitle?: boolean;
} & Pick<ListToolbarProps, 'ImportDialog' | 'ExportDialog' | 'newLink'> &
  Pick<DataGridPremiumProps, 'onRowClick'>;

export const PropertySearchesList: React.FC<PropertySearchesListProps> = ({
  ImportDialog,
  ExportDialog,
  bulkActions,
  defaultFilters,
  queryParamsScope,
  forceEditMode = false,
  toolbarActions,
  onSelectionChange,
  quickFilters,
  onRowClick,
  newLink,
  hideTitle = false,
}) => {
  const { t, locale } = useLocale();
  const { where, order_by, limit, offset, editMode, countLimit } =
    usePropertySearchParams(defaultFilters, queryParamsScope);

  const { data: dictionaryData } = useQuery(GET_PROPERTY_SEARCHES_DICTIONARY);

  const { data, loading, error, refetch } = useQuery(
    GET_PROPERTY_SEARCHES_LIST,
    {
      variables: {
        where,
        order_by,
        limit,
        offset,
      },
      notifyOnNetworkStatusChange: true,
    },
  );

  const { data: countData } = useQuery(GET_PROPERTY_SEARCHES_COUNT, {
    variables: {
      where,
      limit: countLimit,
    },
  });

  const aggregateCount =
    countData?.property_searches_aggregate.aggregate?.count ?? 0;

  const propertySearches: GridRowsProp<PropertySearch> =
    data?.property_searches ?? [];

  const columns = useMemo<GridColDef<PropertySearch>[]>(
    () => [
      {
        field: 'created_at',
        headerName: t('Created'),
        width: 150,
        renderCell: ({ value }) => <TimeAgo dateString={value} />,
      },
      {
        field: 'user.full_name',
        headerName: t('Contact'),
        width: 250,
        renderCell: ({ row }) => <UserInfo user={row.user} />,
      },
      {
        field: 'offer_type',
        headerName: t('Offer Type'),
        width: 100,
        renderCell: ({ value }) => {
          switch (value) {
            case 'buy':
              return t('Buy');
            case 'rent':
              return t('Rent');
            default:
              return '';
          }
        },
      },
      {
        field: 'property_types_in',
        headerName: t('Property Type'),
        sortable: false,
        width: 150,
        renderCell: ({ row }) => {
          const compositeTypes = row.property_types
            .map(({ property_type }) => {
              if (property_type.type === 'composite') {
                return property_type.label;
              }

              if (property_type.composite_type_object != null) {
                return property_type.composite_type_object.label;
              }

              return t('Other');
            })
            .filter(v => v != null);

          return Array.from(new Set(compositeTypes)).join(', ');
        },
      },
      {
        field:
          'sale_price_lte, sale_price_gte, monthly_rent_lte, monthly_rent_gte',
        headerName: t('Price'),
        width: 140,
        valueGetter: (_, row) => ({
          sale_price_lte: row.sale_price_lte,
          sale_price_gte: row.sale_price_gte,
          monthly_rent_lte: row.monthly_rent_lte,
          monthly_rent_gte: row.monthly_rent_gte,
          offer_type: row.offer_type,
        }),
        renderCell: ({ value }) => (
          <>
            {(!!value?.sale_price_lte || !!value?.sale_price_gte) &&
              value.offer_type === 'buy' &&
              formatRange(
                value.sale_price_gte,
                value.sale_price_lte,
                '',
                locale,
              )}
            {(!!value?.monthly_rent_lte || !!value?.monthly_rent_gte) &&
              value.offer_type === 'rent' &&
              formatRange(
                value.monthly_rent_gte,
                value.monthly_rent_lte,
                ` / ${t('month')}`,
                locale,
              )}
          </>
        ),
      },
      {
        field: 'number_of_rooms_gte, number_of_rooms_lte',
        headerName: t('Rooms'),
        width: 100,
        valueGetter: (_, row) => ({
          number_of_rooms_lte: row.number_of_rooms_lte,
          number_of_rooms_gte: row.number_of_rooms_gte,
        }),
        renderCell: ({ value }) => (
          <>
            {formatRange(
              value.number_of_rooms_gte,
              value.number_of_rooms_lte,
              '',
              locale,
            )}
          </>
        ),
      },
      {
        field: 'number_of_bathrooms',
        headerName: t('Bathrooms'),
        width: 100,
        valueGetter: (_, row) => ({
          number_of_rooms_lte: row.number_of_bathrooms_lte,
          number_of_rooms_gte: row.number_of_bathrooms_gte,
        }),
        renderCell: ({ value }) => (
          <>
            {formatRange(
              value.number_of_rooms_gte,
              value.number_of_rooms_lte,
              '',
              locale,
            )}
          </>
        ),
        sortable: false,
      },
      {
        field: 'property_searches_places',
        headerName: t('Places'),
        width: 200,
        valueGetter: (_, row) =>
          row.property_searches_places?.map(p => p.place?.label).join(', '),
        sortable: false,
      },
      {
        field: 'source',
        headerName: t('Source'),
        width: 150,
        valueGetter: (_, row) => ({
          source: row.source,
          aggregator_source: row.aggregator_source,
        }),
        renderCell: ({ value }) => {
          const source = dictionaryData?.sources.find(
            s => s.name === `property_search_source.${value.source}`,
          );
          const aggSource = dictionaryData?.aggregator_sources.find(
            s =>
              s.name ===
              `property_search_aggregator_source.${value.aggregator_source}`,
          );

          return (
            <Stack
              spacing={0.5}
              sx={{
                lineHeight: 'initial',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              {source?.label ?? value.source}
              {value.source === 'aggregator' && (
                <Typography variant="caption">
                  {aggSource?.label ?? value.aggregator_source}
                </Typography>
              )}
            </Stack>
          );
        },
      },
      {
        field: 'notification',
        headerName: t('Notification'),
        width: 100,
        sortable: false,
        valueGetter: (_, row) => {
          switch (row.notification) {
            case 'daily':
              return t('Daily');
            case 'weekly':
              return t('Weekly');
            case 'never':
              return t('Never');
          }

          return '';
        },
      },
    ],
    [t, locale, dictionaryData],
  );

  return (
    <>
      {!hideTitle && <TopbarTitle>{t('Property searches')}</TopbarTitle>}
      <ListToolbar
        tableName="property_searches"
        ImportDialog={ImportDialog}
        ExportDialog={ExportDialog}
        queryParamsScope={queryParamsScope}
        quickFilters={quickFilters}
        bulkActions={bulkActions?.({
          refetch,
        })}
        actions={toolbarActions}
        refetch={refetch}
        newLink={newLink}
        showRealCountOption
        defaultFilterTypeExpanded={
          Object.keys(where).length > 0 ? 'quick' : undefined
        }
      />
      {error ? (
        <Alert severity="error" sx={{ m: 2 }}>
          <pre>{JSON.stringify(error, null, 2)}</pre>
        </Alert>
      ) : (
        <RaDataGrid
          loading={loading}
          rows={propertySearches}
          columns={columns}
          rowCount={aggregateCount}
          approximateRowCount
          checkboxSelection={forceEditMode || editMode}
          queryParamsScope={queryParamsScope}
          onRowClick={onRowClick}
          pageSizeOptions={[25, 50, 100, 500]}
          onRowSelectionModelChange={newSelection => {
            // Exclude-mode selections (header "select all" with server-side
            // paging) carry *excluded* ids, not selected ones, and
            // `propertySearches` is only the current page — acting on it would
            // silently affect just the visible rows. Treat as no explicit pick.
            if (newSelection.type !== 'include') {
              onSelectionChange?.([]);
              return;
            }
            onSelectionChange?.(
              propertySearches.filter(({ id }) => newSelection.ids.has(id)),
            );
          }}
        />
      )}
    </>
  );
};
